import { useCallback, useEffect, useMemo, useRef, useState, use, type ReactNode } from 'react';
import {
  PatientTabsContext,
  type DirtyRegion,
  type DirtyTabSnapshot,
  type OpenConsultationInput,
  type OpenTab,
  type PatientTabsValue,
  type RegionCallbacks,
} from './patient-tabs-context.js';

/**
 * Onglets « façon navigateur » et garde de navigation, portés de la V1 en comportement.
 *
 * Une *région sale* est une portion d'écran qui détient des modifications non enregistrées
 * (le formulaire patient, la saisie d'une consultation…). Elle se déclare elle-même ; la
 * fermeture d'onglet et toute navigation interne s'en servent pour demander quoi faire.
 *
 * Une consultation ouverte reste montée quand on la quitte : sa région sale ne retient donc pas
 * la navigation (la saisie est toujours là). Elle retient la fermeture de son onglet, comme un
 * dossier. Fermer l'onglet retire la vue, pas la ligne en base.
 *
 * Les types et le contexte vivent dans `patient-tabs-context.ts` : ce module-ci ne contient que
 * des composants et des hooks, et peut donc être remplacé à chaud sans casser le contexte.
 */

function regionId(patientId: string, regionKey: string): string {
  return `${patientId}::${regionKey}`;
}

/** Retire un dossier de la table des régions sales sans muter l'état précédent. */
function withoutPatient(
  current: Record<string, string[]>,
  patientId: string,
): Record<string, string[]> {
  return Object.fromEntries(Object.entries(current).filter(([key]) => key !== patientId));
}

export function PatientTabsProvider({ children }: { children: ReactNode }) {
  const [tabs, setTabs] = useState<OpenTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [dirty, setDirty] = useState<Record<string, string[]>>({});
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  const callbacks = useRef(new Map<string, RegionCallbacks>());
  const commitNavigation = useRef<(() => void) | null>(null);
  /**
   * Consultations encore ouvertes. Leur formulaire reste monté : quitter la page ne perd pas la
   * saisie, donc ces régions ne bloquent pas la navigation. Lues au moment du clic, pas au rendu
   * qui a créé le rappel.
   */
  const consultationIdsRef = useRef<ReadonlySet<string>>(new Set());
  consultationIdsRef.current = new Set(
    tabs.flatMap((tab) => (tab.kind === 'consultation' ? [tab.id] : [])),
  );
  /**
   * Miroir synchrone de `dirty`. La garde le lit au moment de la navigation : une navigation
   * lancée juste après un enregistrement réussi ne doit pas voir la région d'avant le rendu.
   */
  const dirtyRef = useRef<Record<string, string[]>>({});

  const updateDirty = useCallback(
    (update: (current: Record<string, string[]>) => Record<string, string[]>) => {
      const next = update(dirtyRef.current);
      dirtyRef.current = next;
      setDirty(next);
    },
    [],
  );

  const openTab = useCallback((patient: { id: string; firstName: string; lastName: string }) => {
    setTabs((current) =>
      current.some((tab) => tab.id === patient.id)
        ? current
        : [
            ...current,
            {
              kind: 'patient',
              id: patient.id,
              firstName: patient.firstName,
              lastName: patient.lastName,
            },
          ],
    );
    setActiveTabId(patient.id);
  }, []);

  const openConsultation = useCallback((tab: OpenConsultationInput) => {
    setTabs((current) => {
      const existing = current.find((item) => item.id === tab.id);
      if (existing?.kind === 'consultation') {
        if (existing.section === tab.section && existing.search === tab.search) return current;
        return current.map((item) =>
          item.kind === 'consultation' && item.id === tab.id
            ? { ...item, section: tab.section, search: tab.search }
            : item,
        );
      }
      if (existing) return current;
      return [
        ...current,
        {
          kind: 'consultation',
          id: tab.id,
          section: tab.section,
          search: tab.search,
          startedAt: tab.startedAt ?? null,
          patientId: tab.patientId ?? '',
        },
      ];
    });
    setActiveTabId(tab.id);
  }, []);

  const updateConsultation = useCallback(
    (id: string, patch: { startedAt: string; patientId: string }) => {
      setTabs((current) => {
        let changed = false;
        const next = current.map((tab) => {
          if (tab.kind !== 'consultation' || tab.id !== id) return tab;
          if (tab.startedAt === patch.startedAt && tab.patientId === patch.patientId) return tab;
          changed = true;
          return { ...tab, startedAt: patch.startedAt, patientId: patch.patientId };
        });
        return changed ? next : current;
      });
    },
    [],
  );

  const closeTab = useCallback(
    (patientId: string) => {
      setTabs((current) => current.filter((tab) => tab.id !== patientId));
      updateDirty((current) => withoutPatient(current, patientId));
      setActiveTabId((current) => (current === patientId ? null : current));
    },
    [updateDirty],
  );

  const registerRegion = useCallback(
    (patientId: string, regionKey: string, entry: RegionCallbacks) => {
      const id = regionId(patientId, regionKey);
      callbacks.current.set(id, entry);
      updateDirty((current) => {
        const keys = current[patientId] ?? [];
        return keys.includes(regionKey)
          ? current
          : { ...current, [patientId]: [...keys, regionKey] };
      });
      return () => {
        // Une inscription plus récente de la même région ne doit pas être effacée par l'ancienne.
        if (callbacks.current.get(id) !== entry) return;
        callbacks.current.delete(id);
        updateDirty((current) => {
          const keys = (current[patientId] ?? []).filter((key) => key !== regionKey);
          if (keys.length === 0) return withoutPatient(current, patientId);
          return { ...current, [patientId]: keys };
        });
      };
    },
    [updateDirty],
  );

  const dirtyRegions = useCallback(
    (patientId: string): DirtyRegion[] =>
      (dirty[patientId] ?? []).map((key) => {
        const entry = callbacks.current.get(regionId(patientId, key));
        return {
          key,
          label: entry?.label ?? key,
          canSave: entry?.save !== undefined,
          canDiscard: entry?.discard !== undefined,
        };
      }),
    [dirty],
  );

  const isDirty = useCallback((patientId: string) => (dirty[patientId]?.length ?? 0) > 0, [dirty]);

  const dirtySnapshot = useCallback((): DirtyTabSnapshot[] => {
    const retained = consultationIdsRef.current;
    return Object.keys(dirty)
      .filter((patientId) => !retained.has(patientId))
      .map((patientId) => {
        const tab = tabs.find((item) => item.id === patientId);
        return {
          patientId,
          patientName:
            tab?.kind === 'patient' ? `${tab.lastName} ${tab.firstName}` : patientId,
          regions: dirtyRegions(patientId),
        };
      });
  }, [dirty, tabs, dirtyRegions]);

  const saveRegion = useCallback(async (patientId: string, regionKey: string) => {
    const entry = callbacks.current.get(regionId(patientId, regionKey));
    if (!entry?.save) throw new Error('region_not_saveable');
    await entry.save();
  }, []);

  const discardRegion = useCallback((patientId: string, regionKey: string) => {
    callbacks.current.get(regionId(patientId, regionKey))?.discard?.();
  }, []);

  const requestNavigation = useCallback((to: string, commit: () => void) => {
    const retained = consultationIdsRef.current;
    const blocking = Object.keys(dirtyRef.current).filter((id) => !retained.has(id));
    if (blocking.length === 0) return false;
    commitNavigation.current = commit;
    setPendingNavigation(to);
    return true;
  }, []);

  const confirmPendingNavigation = useCallback(() => {
    const commit = commitNavigation.current;
    commitNavigation.current = null;
    setPendingNavigation(null);
    const retained = consultationIdsRef.current;
    updateDirty((current) =>
      Object.fromEntries(Object.entries(current).filter(([id]) => retained.has(id))),
    );
    commit?.();
  }, [updateDirty]);

  const cancelPendingNavigation = useCallback(() => {
    commitNavigation.current = null;
    setPendingNavigation(null);
  }, []);

  const value = useMemo<PatientTabsValue>(
    () => ({
      tabs,
      activeTabId,
      openTab,
      openConsultation,
      updateConsultation,
      closeTab,
      setActiveTab: setActiveTabId,
      isDirty,
      dirtyRegions,
      dirtySnapshot,
      saveRegion,
      discardRegion,
      registerRegion,
      requestNavigation,
      pendingNavigation,
      confirmPendingNavigation,
      cancelPendingNavigation,
    }),
    [
      tabs,
      activeTabId,
      openTab,
      openConsultation,
      updateConsultation,
      closeTab,
      isDirty,
      dirtyRegions,
      dirtySnapshot,
      saveRegion,
      discardRegion,
      registerRegion,
      requestNavigation,
      pendingNavigation,
      confirmPendingNavigation,
      cancelPendingNavigation,
    ],
  );

  return <PatientTabsContext value={value}>{children}</PatientTabsContext>;
}

export function usePatientTabs(): PatientTabsValue {
  const context = use(PatientTabsContext);
  if (!context) throw new Error('usePatientTabs doit être utilisé dans un PatientTabsProvider.');
  return context;
}

export interface DirtyRegionOptions {
  patientId: string;
  regionKey: string;
  label: string;
  isDirty: boolean;
  save?: () => Promise<void>;
  discard?: () => void;
}

/**
 * Déclare une région sale tant que `isDirty` est vrai. Le formulaire garde la main sur
 * l'enregistrement et l'abandon ; la coquille ne connaît que les intitulés.
 */
export function useDirtyRegion({
  patientId,
  regionKey,
  label,
  isDirty,
  save,
  discard,
}: DirtyRegionOptions): void {
  // Hors coquille (un test de carte, un rendu isolé), il n'y a pas de navigation à garder.
  const context = use(PatientTabsContext);
  // Les rappels d'enregistrement changent à chaque rendu du formulaire ; on ne veut pas
  // réinscrire la région pour autant. La référence est mise à jour dans un effet, jamais
  // pendant le rendu.
  const latest = useRef({ label, save, discard });

  useEffect(() => {
    latest.current = { label, save, discard };
  }, [label, save, discard]);

  // `registerRegion` est stable. L'objet de contexte, lui, change dès qu'une région est inscrite
  // (la table des régions sales en fait partie). En dépendre réinscrit la région, le nettoyage la
  // retire, et ça recommence : « Maximum update depth ». L'enregistrement part alors en erreur
  // côté client alors que le serveur a déjà répondu.
  const registerRegion = context?.registerRegion;

  useEffect(() => {
    if (registerRegion === undefined || !isDirty) return;
    // Enregistrée avec succès, la région n'est plus sale : elle se retire tout de suite, sans
    // attendre le rendu qui la déclarera propre — une navigation peut suivre immédiatement.
    const unregister = registerRegion(patientId, regionKey, {
      label: latest.current.label,
      save:
        latest.current.save === undefined
          ? undefined
          : async () => {
              await latest.current.save?.();
              unregister();
            },
      discard: latest.current.discard === undefined ? undefined : () => latest.current.discard?.(),
    });
    return unregister;
  }, [registerRegion, isDirty, patientId, regionKey]);
}
